<template>
  <a-select
    :value="modelValue || undefined"
    show-search
    allow-clear
    :filter-option="source.loadAll ? filterLocally : false"
    :options="options"
    :loading="loading"
    :placeholder="placeholder"
    :not-found-content="loading ? 'Searching...' : 'No matches'"
    class="report-lookup"
    @search="onSearch"
    @focus="onFocus"
    @change="(value) => $emit('update:modelValue', value || null)"
  />
</template>

<script>
import axios from "axios";

// How to search and label each kind of record a report filter can pick.
const SOURCES = {
  customer: {
    search: (search) => axios.get("/v1/customers", { params: { search: search || undefined, limit: 20 } }),
    one: (id) => axios.get(`/v1/customers/${encodeURIComponent(id)}`),
    option: (row) => ({ value: row.customerid, label: row.mobile ? `${row.customername} (${row.mobile})` : row.customername }),
  },
  vendor: {
    search: (search) => axios.get("/v1/vendors", { params: { search: search || undefined, limit: 20 } }),
    one: (id) => axios.get(`/v1/vendors/${encodeURIComponent(id)}`),
    option: (row) => ({ value: row.vendorid, label: row.vendorname }),
  },
  item: {
    search: (search) => axios.get("/v1/items", { params: { search: search || undefined, page: 1, page_size: 20 } }),
    one: (id) => axios.get(`/v1/items/${encodeURIComponent(id)}`),
    option: (row) => ({ value: row.itemid, label: row.itemcode ? `${row.itemname} (${row.itemcode})` : row.itemname }),
  },
  category: {
    search: () => axios.get("/v1/category"),
    option: (row) => ({ value: row.categoryid, label: row.categoryname }),
    loadAll: true,
  },
};

function rowsOf(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.data || [];
}

export default {
  name: "ReportLookupSelect",
  props: {
    modelValue: { type: String, default: null },
    type: { type: String, required: true },
    placeholder: { type: String, default: "All" },
  },
  emits: ["update:modelValue"],
  data() {
    return { options: [], loading: false, timer: null, loadedOnce: false };
  },
  computed: {
    source() {
      return SOURCES[this.type];
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (value && !this.options.some((option) => option.value === value)) this.loadSelected(value);
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    async fetch(search) {
      this.loading = true;
      try {
        const response = await this.source.search(search);
        const options = rowsOf(response.data).map(this.source.option);
        const selected = this.options.find((option) => option.value === this.modelValue);
        this.options = selected && !options.some((option) => option.value === selected.value) ? [selected, ...options] : options;
        this.loadedOnce = true;
      } catch (error) {
        console.error(`Failed to load ${this.type} options`, error);
      } finally {
        this.loading = false;
      }
    },
    async loadSelected(id) {
      // A value restored from the address bar: fetch its name so the box shows it.
      if (this.source.loadAll || !this.source.one) {
        await this.fetch("");
        return;
      }
      try {
        const response = await this.source.one(id);
        this.options = [this.source.option(response.data), ...this.options.filter((option) => option.value !== id)];
      } catch (error) {
        this.options = [{ value: id, label: id }, ...this.options];
      }
    },
    filterLocally(input, option) {
      return String(option.label || "").toLowerCase().includes(String(input || "").toLowerCase());
    },
    onFocus() {
      if (!this.loadedOnce) this.fetch("");
    },
    onSearch(search) {
      if (this.source.loadAll) return; // every option is loaded; filterLocally narrows them
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.fetch(search), 300);
    },
  },
};
</script>

<style scoped>
.report-lookup {
  width: 100%;
}
.report-lookup :deep(.ant-select-selector) {
  min-height: 40px;
  align-items: center;
  border-radius: 6px !important;
}
</style>
